import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateWorkOrderJobOpeningMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningMutations";
import { UpdateWorkOrderJobOpeningInput } from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { StepperInput } from "@/lib/Components/Form/Inputs/StepperInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { useTranslations } from "use-intl";
import { WorkOrderJobOpeningItem } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningQuery";

interface EditWorkOrderJobOpeningDialogProps {
  jobOpening: WorkOrderJobOpeningItem;
}

export function EditWorkOrderJobOpeningDialog({
  jobOpening,
}: EditWorkOrderJobOpeningDialogProps) {
  const t = useTranslations("workOrder.jobOpenings");
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(updateWorkOrderJobOpeningMutation);

  const validation = z.object({
    description: z
      .string({ error: t("createDialog.descriptionRequired") })
      .min(1, t("createDialog.descriptionRequired")),
    positions_available: z.number().min(1, t("createDialog.positionsRequired")),
  });

  return (
    <BaseDialog
      title={t("editDialog.title")}
      subtitle={t("editDialog.subtitle")}
    >
      <GenericForm<UpdateWorkOrderJobOpeningInput>
        initialValues={{
          description: jobOpening.description,
          positions_available: jobOpening.positions_available,
          depart_at: jobOpening.depart_at ?? null,
          deliver_at: jobOpening.deliver_at ?? null,
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          await mutateAsync({
            id: jobOpening.id,
            input: values,
          });
          close();
        }}
      >
        <DateInput
          name="depart_at"
          label={t("createDialog.departAt")}
          className="col-span-3"
          optionalLabel
          autoFocus
        />

        <DateInput
          name="deliver_at"
          label={t("createDialog.deliverAt")}
          className="col-span-3"
          optionalLabel
        />

        <StepperInput
          name="positions_available"
          label={t("createDialog.positionsAvailable")}
          className="col-span-3"
        />

        <TextAreaInput
          name="description"
          label={t("createDialog.description")}
          className="col-span-full"
        />

        <DialogSubmitButton text={t("editDialog.save")} />
      </GenericForm>
    </BaseDialog>
  );
}
